import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import MainLayout from "@/components/MainLayout";
import ScrollToTop from "@/components/ScrollToTop";
import { WebsiteBlockGuard } from "@/components/WebsiteBlockGuard";
import Home from "./pages/Home";
import Menu from "./pages/Menu";
import Locations from "./pages/Locations";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ManagementLogin from "./pages/ManagementLogin";
import Dashboard from "./pages/Dashboard";
import Reservations from "./pages/Reservations";
import Catering from "./pages/Catering";
import Blocked from "./pages/Blocked";
import NotFound from "./pages/NotFound";
import Subscription from "./pages/Subscription";
import { useFavicon } from "@/hooks/useFavicon";

const queryClient = new QueryClient();

// Component to handle favicon inside the provider tree
const FaviconHandler = () => {
  useFavicon();
  return null;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
    
      <TooltipProvider>
        <LanguageProvider>
          <BrowserRouter>
            <FaviconHandler />
            <ScrollToTop />
            <WebsiteBlockGuard>
              <Routes>
                {/* Blocked page - accessible to everyone */}
                <Route path="/blocked" element={<Blocked />} />
                
                {/* Dashboard - standalone with sidebar */}
                <Route path="/dashboard/*" element={<Dashboard />} />
                
                {/* Main app routes with Navigation/Footer */}
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/menu" element={<Menu />} />
                  <Route path="/locations" element={<Locations />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/reservations" element={<Reservations />} />
                  <Route path="/catering" element={<Catering />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  <Route path="/management" element={<ManagementLogin />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </WebsiteBlockGuard>
            <Toaster />
            <Sonner />
          </BrowserRouter>
        </LanguageProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
